import React from "react";
import { useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Menu, Building2, UserCheck, Search, Sparkles, MessageCircle, Home, Calendar, Bell, User, Settings, CreditCard, Briefcase, Users, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCurrentPortalMode } from '@/hooks/useCurrentPortalMode';
import { useCompanies } from "@/hooks/auth";
import { CompanyAvatar } from "@/components/ui/company-avatar";
import { useResponsiveBreakpoints } from "@/hooks/use-mobile";
import { useLocation } from 'react-router-dom';
import { useTranslation } from "@/hooks/useTranslation";
import { useChatUnreadCount } from "@/hooks/useChatUnreadCount";

// Mode definitions - must be created inside component to access t()
const getModes = (t: any) => [{
  key: 'talent',
  label: t('talent'),
  icon: UserCheck,
}, {
  key: 'company',
  label: t('company'),
  icon: Building2,
}, {
  key: 'recruiter',
  label: t('recruiter'),
  icon: Search,
}, {
  key: 'internal',
  label: t('internal'),
  icon: Sparkles,
}];

// Page name utility function
const getPageName = (pathname: string, urlMode: string, displayName: string, t: any, modes: any[]) => {
  // Special non-workspace routes
  if (pathname === '/') return { name: t('pages.home'), icon: Home };
  if (pathname === '/evee-ai') return { name: t('pages.evee_ai'), icon: Sparkles };
  if (pathname === '/evee-ai-agent') return { name: t('pages.ai_agent'), icon: Sparkles };
  if (pathname === '/calendar') return { name: t('pages.calendar'), icon: Calendar };
  if (pathname === '/notifications') return { name: t('pages.notifications'), icon: Bell };
  if (pathname === '/account/profile') return { name: t('pages.profile'), icon: User };
  if (pathname === '/account/settings') return { name: t('pages.settings'), icon: Settings };
  if (pathname === '/account/billing') return { name: t('pages.billing'), icon: CreditCard };
  
  // Company workspace routes
  if (pathname.includes('/workspace/company/hiring-center')) return { name: t('pages.hiring_center'), icon: Briefcase };
  if (pathname.includes('/workspace/company/candidates')) return { name: t('pages.candidates'), icon: Users };
  if (pathname.includes('/workspace/company/company-profile')) return { name: t('pages.company_profile'), icon: Building2 };
  if (pathname.includes('/workspace/company/team-management')) return { name: t('pages.team'), icon: Users };
  if (pathname.includes('/workspace/company/job-board')) return { name: t('pages.job_board'), icon: Briefcase };
  
  // Talent workspace routes
  if (pathname.includes('/workspace/talent/profile')) return { name: t('pages.profile'), icon: User };
  if (pathname.includes('/workspace/talent/applications')) return { name: t('pages.applications'), icon: Briefcase };
  if (pathname.includes('/workspace/talent/job-search')) return { name: t('pages.job_search'), icon: Search };
  if (pathname.includes('/workspace/talent/community')) return { name: t('pages.community'), icon: Users };
  
  // Recruiter workspace routes
  if (pathname.includes('/workspace/recruiter/network')) return { name: t('pages.network'), icon: Users };
  if (pathname.includes('/workspace/recruiter/earnings')) return { name: t('pages.earnings'), icon: CreditCard };
  if (pathname.includes('/workspace/recruiter/referral-links')) return { name: t('referral_links'), icon: Link2 };
  
  // Internal workspace routes
  if (pathname.includes('/workspace/internal/')) return { name: t('pages.internal_portal'), icon: Sparkles };
  
  // Default to workspace mode name
  const modeIcon = modes.find(m => m.key === urlMode)?.icon || UserCheck;
  return { name: displayName, icon: modeIcon };
};

export const MobileHeaderBar = () => {
  const { open, toggleSidebar } = useSidebar();
  const { isTinyMobile } = useResponsiveBreakpoints();
  const { t } = useTranslation('navigation');
  const { unreadCount, hasUnread } = useChatUnreadCount();
  
  const urlMode = useCurrentPortalMode();
  const { selectedCompany } = useCompanies();
  const UNIFIED_MODES = getModes(t);
  const currentModeData = UNIFIED_MODES.find(m => m.key === urlMode);
  
  // Display logic: Show company name when in company mode
  const displayName = urlMode === 'company' && selectedCompany?.name 
    ? selectedCompany.name 
    : currentModeData?.label || t('talent');

  const location = useLocation();
  const pageInfo = getPageName(location.pathname, urlMode, displayName, t, UNIFIED_MODES);

  // Handle chat toggle via custom event
  const handleChatToggle = () => {
    window.dispatchEvent(new CustomEvent('toggle-mobile-chat'));
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[40]" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="bg-background border-b border-border shadow-sm px-4 py-3 flex items-center justify-between">
        {/* Menu Button - Icon only */}
        <Button 
          type="button"
          onClick={toggleSidebar}
          className={cn(
            "h-8 w-8 p-0 rounded-lg min-h-[44px] min-w-[44px]",
            "hover:bg-accent transition-colors duration-200",
            open && "opacity-50"
          )}
          aria-label="Toggle navigation menu"
          variant="ghost"
        >
          <Menu className="h-4 w-4" />
        </Button>

        {/* Center: Page Name Display */}
        <div className="flex items-center gap-1 flex-1 justify-center px-1 min-w-0">
          <pageInfo.icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className={cn(
            "font-medium text-muted-foreground truncate",
            isTinyMobile ? "text-xs max-w-[80px]" : "text-xs sm:text-sm max-w-[120px] sm:max-w-[180px]"
          )}>
            {pageInfo.name}
          </span>
        </div>

        {/* Chat Button - Icon only with unread badge */}
        <Button
          type="button"
          onClick={handleChatToggle}
          className={cn(
            "relative h-8 w-8 p-0 rounded-lg min-w-[44px] min-h-[44px]",
            "hover:bg-accent transition-colors duration-200"
          )}
          aria-label={hasUnread ? `Open chat - ${unreadCount} unread` : "Open chat"}
          variant="ghost"
        >
          <MessageCircle className="h-4 w-4" />
          {hasUnread && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] border-2 border-background"
            >
              {unreadCount > 99 ? '99' : unreadCount}
            </Badge>
          )}
        </Button>
      </div>
    </div>
  );
};